import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { firebaseConfig, firebaseConfigIsPlaceholder } from '@/lib/firebase-config'

// Firebase is now used for ONE thing only: LOGIN (Google / email+password / password reset /
// email verification). All data - profiles, posts, chats, wallet, everything - lives in
// Cloudflare D1 and is accessed through lib/db.ts -> /api/db. File uploads go to R2.
// Config lives in lib/firebase-config.ts (paste your Firebase project's web config there).
if (typeof window !== 'undefined' && firebaseConfigIsPlaceholder) {
  console.error('Firebase is not configured: open lib/firebase-config.ts and paste your Firebase web config.')
}

// Only initialise in the browser: Next still server-renders 'use client' components once for
// the initial HTML, and there is no reason to construct the Auth SDK inside the Worker for that.
const isBrowser = typeof window !== 'undefined'
const app: FirebaseApp | null = isBrowser ? (getApps().length ? getApp() : initializeApp(firebaseConfig)) : null

// App Check with reCAPTCHA v3 - fully automatic/invisible bot protection on every
// login/signup/reset-password call. No checkbox, no user interaction: it silently scores
// each request and Firebase Auth rejects the ones that look automated. Requires a reCAPTCHA
// v3 site key registered against this Firebase project (Firebase Console -> App Check) and
// set as NEXT_PUBLIC_RECAPTCHA_SITE_KEY. Without that key it's skipped, so auth keeps working.
const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ''
if (isBrowser && recaptchaSiteKey) {
  try {
    initializeAppCheck(app!, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    })
  } catch {
    // Non-fatal - if App Check fails to init (e.g. bad key), auth should still work.
  }
}

export const firebaseAuth = (isBrowser ? getAuth(app!) : ({} as Auth))
